import { mm } from './units.js';
import { defaultCabinetParams, type CabinetParams, type FrontRow } from './params.js';
import type { ParametricPresetContent, PresetDerived, PresetParamDef } from './preset.js';

export interface SystemPresetDef {
  /** Stable id — never change once released. */
  id: string;
  name: string;
  category: 'shelf' | 'base' | 'wall' | 'tall' | 'drawers' | 'wardrobe' | 'living' | 'kitchen';
  description: string;
  tags: string[];
  content: ParametricPresetContent;
}

const L = (key: string, label: string, path: string, def: number, min: number, max: number, step = 1): PresetParamDef => ({
  key,
  label,
  path,
  dim: 'length',
  default: mm(def),
  min: mm(min),
  max: mm(max),
  step: mm(step),
});
const C = (key: string, label: string, path: string, def: number, min: number, max: number): PresetParamDef => ({ key, label, path, dim: 'count', default: def, min, max, step: 1 });

const W = (def: number, min: number, max: number) => L('width', 'Širina', 'width', def, min, max);
const H = (def: number, min: number, max: number) => L('height', 'Visina korpusa', 'height', def, min, max);
const D = (def: number, min: number, max: number) => L('depth', 'Dubina', 'depth', def, min, max);

const fronts = (rows: FrontRow[], mounting: 'overlay' | 'inset' = 'overlay'): CabinetParams['fronts'] => ({ mounting, gapOuter: mm(1.5), gapBetween: mm(3), rows });

function preset(
  id: string,
  name: string,
  category: SystemPresetDef['category'],
  description: string,
  tags: string[],
  base: Partial<CabinetParams>,
  params: PresetParamDef[],
  derived: PresetDerived[] = [],
  notes: string[] = [],
): SystemPresetDef {
  const b = defaultCabinetParams(base);
  // Defaults of exposed params are written into the base, so base and defaults always agree.
  const content: ParametricPresetContent = { schemaVersion: 1, kind: 'parametric', base: b, params, derived, materials: {}, notes };
  return { id, name, category, description, tags, content };
}

const noShelves = { count: 0, frontSetback: mm(2), sideClearance: mm(0.5) };

export const SYSTEM_PRESETS: SystemPresetDef[] = [
  preset(
    'sys-open-shelf',
    'Otvorena polica',
    'shelf',
    'Otvorena polica sa podesivim brojem polica i pregrada, leđa nalijepljena.',
    ['polica', 'otvoreno'],
    { width: mm(800), height: mm(1800), depth: mm(320), back: { mode: 'overlay', grooveInset: 0, grooveDepth: mm(8), clearance: mm(1) }, shelves: { count: 4, frontSetback: 0, sideClearance: mm(0.5) } },
    [W(800, 300, 1200), H(1800, 400, 2600), D(320, 200, 500), C('shelves', 'Broj polica', 'shelves.count', 4, 0, 10), C('dividers', 'Broj pregrada', 'dividers', 0, 0, 4)],
  ),
  preset(
    'sys-door-1',
    'Ormarić sa jednim vratima',
    'base',
    'Donji ormarić sa jednim vratima i podesivom policom, na nogicama.',
    ['vrata', 'ormarić'],
    { width: mm(450), legsHeight: mm(100), fronts: fronts([{ kind: 'door-left', height: null }]) },
    [W(450, 250, 600), H(720, 300, 1000), D(560, 300, 650), C('shelves', 'Broj polica', 'shelves.count', 1, 0, 4), L('legs', 'Nogice', 'legsHeight', 100, 0, 200)],
  ),
  preset(
    'sys-door-2',
    'Ormarić sa dvoja vrata',
    'base',
    'Donji ormarić sa dvoja vrata (simetrični zazori), na nogicama.',
    ['vrata', 'ormarić'],
    { width: mm(800), legsHeight: mm(100), fronts: fronts([{ kind: 'double-door', height: null }]) },
    [W(800, 500, 1200), H(720, 300, 1000), D(560, 300, 650), C('shelves', 'Broj polica', 'shelves.count', 1, 0, 4), L('legs', 'Nogice', 'legsHeight', 100, 0, 200)],
  ),
  preset(
    'sys-drawers-3',
    'Komoda sa tri ladice',
    'drawers',
    'Komoda sa tri jednake ladice. Vodilice su generički ručni model — zamijenite stvarnim sistemom kad ga unesete u katalog.',
    ['ladice', 'komoda'],
    { width: mm(800), legsHeight: mm(100), shelves: noShelves, fronts: fronts([{ kind: 'drawer', height: null }, { kind: 'drawer', height: null }, { kind: 'drawer', height: null }]) },
    [W(800, 400, 1200), H(720, 450, 1000), D(560, 500, 650), L('legs', 'Nogice', 'legsHeight', 100, 0, 200)],
    [],
    ['Generička vodilica: bočni razmak 12,5 mm po strani, dužina 450 mm.'],
  ),
  preset(
    'sys-drawer-door',
    'Kombinovani element ladica/vrata',
    'base',
    'Gornja ladica i dvoja vrata ispod.',
    ['ladica', 'vrata', 'kombinovano'],
    { width: mm(600), legsHeight: mm(100), shelves: noShelves, fronts: fronts([{ kind: 'drawer', height: mm(140) }, { kind: 'double-door', height: null }]) },
    [W(600, 450, 1000), H(720, 500, 1000), D(560, 500, 650), L('drawerFront', 'Visina fronte ladice', 'fronts.rows.0.height', 140, 100, 300)],
  ),
  preset(
    'sys-kitchen-wall',
    'Viseći kuhinjski element',
    'wall',
    'Viseći element sa dvoja vrata, plitka dubina.',
    ['kuhinja', 'viseći'],
    { width: mm(600), height: mm(720), depth: mm(320), fronts: fronts([{ kind: 'double-door', height: null }]) },
    [W(600, 300, 1000), H(720, 300, 1000), D(320, 250, 400), C('shelves', 'Broj polica', 'shelves.count', 1, 0, 3)],
  ),
  preset(
    'sys-kitchen-base',
    'Donji kuhinjski element',
    'kitchen',
    'Donji kuhinjski element 720 mm na sokli/nogicama 100 mm (radna ploča nije dio korpusa).',
    ['kuhinja', 'donji'],
    { width: mm(600), height: mm(720), depth: mm(560), legsHeight: mm(100), topConstruction: 'none', fronts: fronts([{ kind: 'double-door', height: null }]) },
    [W(600, 300, 1200), D(560, 450, 650), C('shelves', 'Broj polica', 'shelves.count', 1, 0, 3), L('legs', 'Nogice', 'legsHeight', 100, 80, 200)],
    [],
    ['Bez gornje ploče korpusa — gornji dio zatvara zajednička radna ploča (Faza 7).'],
  ),
  preset(
    'sys-kitchen-sink',
    'Sudoperski element',
    'kitchen',
    'Donji element za sudoper: bez leđa i bez police radi instalacija; servisni prostor je cijela unutrašnjost.',
    ['kuhinja', 'sudoper'],
    { width: mm(800), height: mm(720), depth: mm(560), legsHeight: mm(100), topConstruction: 'none', back: { mode: 'none', grooveInset: mm(10), grooveDepth: mm(8), clearance: mm(1) }, shelves: noShelves, fronts: fronts([{ kind: 'double-door', height: null }]) },
    [W(800, 600, 1200), D(560, 500, 650), L('legs', 'Nogice', 'legsHeight', 100, 80, 200)],
    [],
    ['Servisni prostor: bez leđa i police. Izrez za sudoper/sifon i zaštita rezova od vlage dolaze sa obradama (Faza 8).'],
  ),
  preset(
    'sys-tall',
    'Visoki ormarić',
    'tall',
    'Visoki ormarić sa dvoja vrata jedna iznad drugih i policama.',
    ['visoki', 'ostava'],
    { width: mm(600), height: mm(2000), depth: mm(560), legsHeight: mm(100), shelves: { count: 4, frontSetback: mm(2), sideClearance: mm(0.5) }, fronts: fronts([{ kind: 'door-left', height: null }, { kind: 'door-left', height: mm(700) }]) },
    [W(600, 400, 800), H(2000, 1400, 2400), D(560, 350, 650), C('shelves', 'Broj polica', 'shelves.count', 4, 0, 8), L('lowerDoor', 'Visina donjih vrata', 'fronts.rows.1.height', 700, 400, 1200)],
  ),
  preset(
    'sys-wardrobe-2',
    'Dvokrilni garderober',
    'wardrobe',
    'Garderober sa dvoja vrata i gornjom policom (šipka za vješanje je okov, dodaje se ručno).',
    ['garderober', 'ormar'],
    { width: mm(1000), height: mm(2100), depth: mm(600), plinthHeight: mm(80), shelves: { count: 1, frontSetback: mm(2), sideClearance: mm(0.5) }, fronts: fronts([{ kind: 'double-door', height: null }]) },
    [W(1000, 700, 1200), H(2100, 1600, 2500), D(600, 500, 650), L('plinth', 'Sokla', 'plinthHeight', 80, 50, 150)],
  ),
  preset(
    'sys-wardrobe-dividers',
    'Garderober sa pregradama i policama',
    'wardrobe',
    'Garderober sa pregradom i više polica po pregradi.',
    ['garderober', 'pregrade'],
    { width: mm(1200), height: mm(2100), depth: mm(600), plinthHeight: mm(80), dividers: 1, shelves: { count: 5, frontSetback: mm(2), sideClearance: mm(0.5) }, fronts: fronts([{ kind: 'double-door', height: null }]) },
    [W(1200, 800, 1600), H(2100, 1600, 2500), D(600, 450, 650), C('dividers', 'Broj pregrada', 'dividers', 1, 0, 3), C('shelves', 'Police po pregradi', 'shelves.count', 5, 0, 8)],
  ),
  preset(
    'sys-tv',
    'TV komoda',
    'living',
    'Niska široka komoda sa otvorenim pregradama, na nogicama.',
    ['dnevni boravak', 'tv'],
    { width: mm(1800), height: mm(450), depth: mm(400), legsHeight: mm(120), dividers: 2, shelves: { count: 1, frontSetback: 0, sideClearance: mm(0.5) } },
    [W(1800, 1000, 2400), H(450, 350, 700), D(400, 300, 550), C('dividers', 'Broj pregrada', 'dividers', 2, 0, 5), L('legs', 'Nogice', 'legsHeight', 120, 0, 250)],
  ),
];
